import React from "react";

// Status color function for Completed and Cancelled
function getStatusColor(status) {
  const statusLower = status?.toLowerCase();
  
  if (statusLower === "completed") {
    return {
      color: "#059669",
      bg: "#d1fae5",
      border: "#a7f3d0",
    };
  }
  if (statusLower === "cancelled") {
    return {
      color: "#dc2626",
      bg: "#fee2e2",
      border: "#fecaca",
    };
  }
  // Default for Active/Scheduled
  return {
    color: "#2563eb",
    bg: "#dbeafe",
    border: "#bfdbfe",
  };
}

// Skeleton loader
function SkeletonItem() {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        padding: "14px 20px",
        borderBottom: "1px solid #f1f5f9",
        gap: 14,
      }}
    >
      <div
        className="skeleton"
        style={{
          width: 44,
          height: 44,
          borderRadius: "50%",
          flexShrink: 0,
        }}
      />
      <div
        style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}
      >
        <div
          className="skeleton"
          style={{
            height: 16,
            width: "40%",
            borderRadius: 4,
          }}
        />
        <div
          className="skeleton"
          style={{
            height: 13,
            width: "55%",
            borderRadius: 4,
          }}
        />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
        <div
          className="skeleton"
          style={{
            height: 18,
            width: 55,
            borderRadius: 4,
          }}
        />
        <div
          className="skeleton"
          style={{
            height: 18,
            width: 65,
            borderRadius: 8,
          }}
        />
      </div>
    </div>
  );
}

function DocSchedule({ schedules, isLoading, isSuccess }) {
  return (
    <div
      style={{
        background: "#ffffff",
        borderRadius: 14,
        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
        border: "1px solid #e5e7eb",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        marginBottom:10
      }}
    >
      {/* Header */}
      <div
        style={{
          borderBottom: "1px solid #e5e7eb",
          padding: "16px 20px",
          fontWeight: 600,
          fontSize: "15px",
          color: "#111827",
        }}
      >
        Today's Appointments
      </div>

      {/* Loading skeleton UI */}
      {isLoading && (
        <div>
          {[...Array(5)].map((_, i) => (
            <SkeletonItem key={i} />
          ))}
        </div>
      )}

      {/* No Data */}
      {!isLoading && (!isSuccess || !schedules || schedules.length === 0) && (
        <div
          style={{
            height: 200,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: 6,
            color: "#9ca3af",
            fontWeight: 500,
            fontSize: "14px",
          }}
        >
          <svg
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            style={{ opacity: 0.4 }}
          >
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
          No Appointments Today
        </div>
      )}

      {/* Data List */}
      {isSuccess && schedules?.length > 0 && (
        <div
          style={{
            maxHeight: 400,
            overflowY: "auto",
          }}
          className="custom-scrollbar"
        >
          {schedules.map((apt, idx) => {
            const statusColors = getStatusColor(apt.status);

            return (
              <div
                key={idx}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "14px 20px",
                  borderBottom:
                    idx === schedules.length - 1 ? "none" : "1px solid #f1f5f9",
                  transition: "background-color 0.15s ease",
                }}
                className="appointment-row"
              >
                {/* Avatar */}
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    fontWeight: 600,
                    color: "#ffffff",
                    fontSize: "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: 14,
                    flexShrink: 0,
                    textTransform: "uppercase",
                  }}
                >
                  {apt.patientName[0]}
                </div>

                {/* Main Info */}
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    minWidth: 0,
                  }}
                >
                  {/* Patient Name */}
                  <div
                    style={{
                      fontWeight: 600,
                      color: "#111827",
                      fontSize: "14px",
                    }}
                  >
                    {apt.patientName}
                  </div>

                  {/* Diagnosis */}
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#6b7280",
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {apt.diagnosis || "No Diagnosis"} . {apt.appointmentType || "No Type"}
                  </div>
                </div>

                {/* Time & Status Column */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: 6,
                    marginLeft: 14,
                  }}
                >
                  {/* Time Slot */}
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#111827",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {apt.timeSlot}
                  </div>

                  {/* Status Badge */}
                  <div
                    style={{
                      fontSize: "11px",
                      fontWeight: 600,
                      color: statusColors.color,
                      background: statusColors.bg,
                      border: `1px solid ${statusColors.border}`,
                      borderRadius: 8,
                      padding: "4px 10px",
                      textTransform: "capitalize",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {apt.status}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Styles */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }

        @keyframes wave {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }

        .skeleton {
          background: linear-gradient(
            90deg,
            #f3f4f6 0%,
            #e5e7eb 20%,
            #f3f4f6 40%,
            #f3f4f6 100%
          );
          background-size: 200% 100%;
          animation: wave 1.8s linear infinite;
        }

        .appointment-row:hover {
          background-color: #f9fafb;
        }
      `}</style>
    </div>
  );
}

export default DocSchedule;
